import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useAllOrders, useRtoAccept, useRtoReject } from "../../../../api/orders/orders";
import { useLoader } from "../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const RejectRto = ({ rtoStatus = "rejected" }) => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [rejectFile, setRejectFile] = useState(null);
  const [rejectOrderId, setRejectOrderId] = useState(null);

  const { setLoading } = useLoader();

  const { data: allOrdersData, isLoading: isAllLoading, isError } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    rtoStatus,
    search: searchQuery,
  });

  const { mutate: acceptOrder } = useRtoAccept();
  const { mutate: rejectOrder } = useRtoReject();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  const orders = allOrdersData?.orders || [];
  const totalPages = allOrdersData?.totalPages || 1;
  const isLoading = isAllLoading;

  const handleAccept = (awb) => {
    acceptOrder(awb);
  };

  const handleRejectOpen = (awb) => {
    setRejectOrderId(awb);
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectRemark.trim()) return;

    const payload = { orderId: rejectOrderId, remark: rejectRemark };
    rejectOrder(payload);

    setRejectModalOpen(false);
    setRejectRemark("");
    setRejectFile(null);
    setRejectOrderId(null);
  };

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Number / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">Courier Details</th>
              <th className="p-3">RTO Info</th>
              {/* <th className="p-3">Actions</th> */}
            </tr>
          </thead>

          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={order.awbNumber || index} className="hover:bg-gray-50">
                  {/* Order Date */}
                  <td className="p-4">{new Date(order.createdAt).toLocaleDateString()}</td>

                  {/* Manifest / Shipping Status */}
                  <td className="p-4">
                    <div className="font-medium text-blue-600">{order.OrderNumber}</div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatusText === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatusText === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatusText === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>

                  {/* Payment */}
                  <td className="p-4">
                    ₹{order.InvoiceAmount}
                    <div className="text-xs mt-1 bg-green-100 text-green-700 inline-block px-2 py-1 rounded">
                      {order.PaymentType}
                    </div>
                  </td>

                  {/* Products */}
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>

                  {/* Customer / Contact */}
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>

                  {/* Address */}
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>

                  {/* Courier Details */}
                  <td className="p-4 space-y-2">
                     <p className="font-medium">{order.awbNumber}</p>
                    <p>{order.courier}</p>
                  </td>

                  {/* RTO Info */}
                  <td className="p-4 text-sm space-y-1">
                    {order.rtoCaption && (
                      <p> 
                        <strong>Caption:</strong> {order.rtoCaption}
                      </p>
                    )}
                    {order.rtoPhoto && (
                      <p>
                        <strong>Photo:</strong>{" "}
                        <a href={order.rtoPhoto} target="_blank" rel="noopener noreferrer">
                          View
                        </a>
                      </p>
                    )}
                    {order.rtoRemark && (
                      <p>
                        <strong>Remark:</strong> {order.rtoRemark}
                      </p>
                    )}
                    {!order.rtoCaption && !order.rtoPhoto && !order.rtoRemark && (
                      <span className="text-gray-400 text-xs">No RTO info</span>
                    )}
                  </td>

                  {/* Actions */}
                  {/* <td className="p-4 flex justify-center items-center space-x-2">
                    {order.rtoStatus !== "accepted" && order.rtoStatus !== "rejected" ? (
                      <>
                        <Button
                          type="primary"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleAccept(order.awbNumber)}
                        >
                          Accept
                        </Button>
                        <Button
                          danger
                          className="bg-red-600 hover:bg-red-700"
                          onClick={() => handleRejectOpen(order.awbNumber)}
                        >
                          Reject
                        </Button>
                      </>
                    ) : (
                      <span className="text-gray-400 text-xs">Action completed</span>
                    )}
                  </td> */}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center py-6 text-gray-400">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Reject Modal */}
      <Modal
        title="Reject Order"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setRejectModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="reject" danger type="primary" onClick={handleRejectConfirm}>
            Reject
          </Button>,
        ]}
      >
        <p className="mb-2">Enter reason for rejection:</p>
        <Input.TextArea
          rows={4}
          value={rejectRemark}
          onChange={(e) => setRejectRemark(e.target.value)}
          placeholder="Enter rejection remark..."
        />
        <div className="mt-4">
          <Upload
            beforeUpload={(file) => {
              setRejectFile(file);
              return false; // prevent auto upload
            }}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Upload Photo</Button>
          </Upload>
          {rejectFile && (
            <p className="mt-2 text-sm text-gray-600">Selected file: {rejectFile.name}</p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default RejectRto;
